import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/auth-context";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Avatar,
  Button,
  IconButton,
  CircularProgress,
  useTheme,
  TextField,
} from "@mui/material";
import { ArrowLeft } from "lucide-react";
import { Delete } from "@mui/icons-material";
import axiosInstance from "../../api/axios-config";
import imageCompression from "browser-image-compression";
import { PhoneNumberInput } from "../../components/common/PhoneInput";

const ProfileEditPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [imagePreview, setImagePreview] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [fullname, setFullname] = useState("");
  const [phone, setPhone] = useState("");

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setFullname(user.fullname || "");
      setPhone(user.phone || "");
      if (user.profilePicture) {
        setImagePreview(user.profilePicture);
      }
    }
  }, [user]);

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    setIsUploading(true);

    try {
      // Client-side compression
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 500,
        useWebWorker: true,
        fileType: "image/jpeg" as const,
      };

      const compressedFile = await imageCompression(file, options);

      // Preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(compressedFile);

      // Upload to S3
      const extension = "jpg";
      const { data } = await axiosInstance.get("/aws/s3-signed-url/put", {
        params: { folder: "profiles", extension },
      });

      await fetch(data.url, {
        method: "PUT",
        body: compressedFile,
        headers: { "Content-Type": "image/jpeg" },
      });

      // Update user profile picture
      await axiosInstance.patch(`/users/${user.id}`, {
        profilePicture: data.path,
      });

      // Refresh user data to update the context
      await refreshUser();
    } catch (error) {
      console.error("Error uploading image:", error);
      // Reset preview on error
      setImagePreview(user?.profilePicture || "");
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!user) return;

    setIsUploading(true);
    try {
      await axiosInstance.patch(`/users/${user.id}`, {
        profilePicture: null,
      });

      setImagePreview("");
      await refreshUser();
    } catch (error) {
      console.error("Error removing photo:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    setIsSaving(true);
    try {
      await axiosInstance.patch(`/users/${user.id}`, {
        fullname,
        phone: phone || null,
      });

      await refreshUser();
      navigate("/profile");
    } catch (error) {
      console.error("Error saving profile:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate("/profile")}>
          <ArrowLeft />
        </IconButton>
        <Typography variant="h4" fontWeight={600}>
          {t('profile.edit.title', 'Modifier mon profil')}
        </Typography>
      </Stack>

      <Card elevation={2}>
        <CardContent sx={{ p: 4 }}>
          {/* Profile Picture Upload */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <input
              accept="image/*"
              style={{ display: "none" }}
              id="profile-picture-upload"
              type="file"
              onChange={handleImageChange}
              disabled={isUploading}
            />

            <Stack
              direction="column"
              spacing={1}
              justifyContent="center"
              alignItems="center"
            >
              <label htmlFor="profile-picture-upload">
                <IconButton
                  color="primary"
                  component="span"
                  disabled={isUploading}
                  sx={{ p: 0 }}
                >
                  <Avatar
                    src={imagePreview || undefined}
                    sx={{
                      width: 120,
                      height: 120,
                      cursor: "pointer",
                      border: `3px solid ${theme.palette.primary.main}`,
                      bgcolor: theme.palette.primary.main,
                      fontSize: "3rem",
                      "&:hover": { opacity: 0.8 },
                    }}
                  >
                    {isUploading ? (
                      <CircularProgress size={40} sx={{ color: "white" }} />
                    ) : (
                      user.fullname ? user.fullname.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()
                    )}
                  </Avatar>
                </IconButton>
              </label>

              {imagePreview && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  onClick={handleRemovePhoto}
                  sx={{
                    cursor: isUploading ? "default" : "pointer",
                    color: theme.palette.error.main,
                    opacity: isUploading ? 0.5 : 1,
                    "&:hover": { opacity: isUploading ? 0.5 : 0.7 },
                  }}
                >
                  <Delete fontSize="small" />
                  <Typography variant="caption" color="error">
                    {t('profile.removePhoto', 'Supprimer la photo')}
                  </Typography>
                </Stack>
              )}
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('profile.photoHint', "Cliquez pour changer votre photo de profil")}
            </Typography>
          </Box>

          {/* Form Fields */}
          <Stack spacing={3}>
            <TextField
              label={t('profile.fullName', 'Nom complet')}
              value={fullname}
              onChange={(e) => setFullname(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label={t('profile.email', 'Email')}
              value={user.email}
              fullWidth
              disabled
              InputLabelProps={{ shrink: true }}
              helperText={t('profile.edit.emailHint', "L'email ne peut pas être modifié")}
            />

            <PhoneNumberInput
              label={t('profile.phone', 'Téléphone')}
              value={phone}
              onChange={(value) => setPhone(value ?? '')}
              fullWidth
            />
          </Stack>

          {/* Actions */}
          <Stack direction="row" spacing={2} sx={{ mt: 4 }} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => navigate("/profile")}
              disabled={isSaving}
            >
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving || isUploading}
            >
              {isSaving ? <CircularProgress size={24} /> : t('common.save', 'Enregistrer')}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProfileEditPage;
